import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdOutlineAccountBalanceWallet } from 'react-icons/md';

const PRESET_AMOUNTS = [100, 250, 500, 1000];
const MIN_AMOUNT = 10;
const MAX_AMOUNT = 5000;

const WalletTopUp = () => {
    const nav = useNavigate();
    const [selectedAmount, setSelectedAmount] = useState<number | null>(250);
    const [customAmount, setCustomAmount] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const amount = customAmount ? parseFloat(customAmount) : selectedAmount || 0;
    const isValid = amount >= MIN_AMOUNT && amount <= MAX_AMOUNT;

    const handleConfirm = async () => {
        if (!isValid) {
            setError(`Le montant doit être compris entre ${MIN_AMOUNT}€ et ${MAX_AMOUNT}€.`);
            return;
        }
        setError('');
        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(
                `${CONFIG.apiUrl}/stripe/checkout-wallet-topup`,
                { amount },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            window.location.href = res.data.url;
        } catch (err: any) {
            setError(err.response?.data?.message || 'Erreur lors de la création du paiement.');
            setSubmitting(false);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            {/* Header */}
            <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-leadrs-brown-soft">
                        <MdOutlineAccountBalanceWallet className="h-5 w-5 text-leadrs-brown-focus" />
                    </div>
                    <div>
                        <h2 className="font-crimson text-2xl font-semibold leading-tight text-leadrs-text">
                            Ajouter des crédits
                        </h2>
                        <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                            Choisissez le montant à créditer sur votre cagnotte. Vous serez redirigé vers une page de paiement sécurisée.
                        </p>
                    </div>
                </div>
            </div>

            {/* Amount picker */}
            <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                <p className="mb-4 font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">
                    Montant à créditer
                </p>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {PRESET_AMOUNTS.map((preset) => (
                        <button
                            key={preset}
                            type="button"
                            onClick={() => {
                                setSelectedAmount(preset);
                                setCustomAmount('');
                                setError('');
                            }}
                            className={`rounded-lg border px-4 py-3 text-center font-geist text-base font-medium transition-colors ${
                                selectedAmount === preset && !customAmount
                                    ? 'border-leadrs-brand bg-leadrs-purple-soft text-leadrs-purple-focus'
                                    : 'border-leadrs-stroke text-leadrs-text hover:border-leadrs-brand'
                            }`}
                        >
                            {preset}€
                        </button>
                    ))}
                </div>

                <div className="mt-4">
                    <label className="mb-1 block font-geist text-sm text-leadrs-text-soft">
                        Ou saisissez un montant personnalisé
                    </label>
                    <input
                        type="number"
                        min={MIN_AMOUNT}
                        max={MAX_AMOUNT}
                        placeholder={`Entre ${MIN_AMOUNT}€ et ${MAX_AMOUNT}€`}
                        value={customAmount}
                        onChange={(e) => {
                            setCustomAmount(e.target.value);
                            setSelectedAmount(null);
                            setError('');
                        }}
                        className="w-full rounded-lg border border-leadrs-stroke px-4 py-2.5 font-geist text-base text-leadrs-text outline-none focus:border-leadrs-brand"
                    />
                </div>

                {error && <p className="mt-3 font-geist text-sm text-leadrs-wrong">{error}</p>}

                <button
                    type="button"
                    disabled={submitting || !isValid}
                    onClick={handleConfirm}
                    className="mt-6 w-full rounded-lg bg-leadrs-yellow-soft px-5 py-3 font-geist text-sm font-medium text-leadrs-yellow-focus transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:bg-leadrs-disabled disabled:text-leadrs-text-soft"
                >
                    {submitting ? 'Redirection...' : `Continuer vers le paiement${amount > 0 ? ` ${amount}€` : ''}`}
                </button>

                <button
                    type="button"
                    onClick={() => nav('/admin/wallet')}
                    className="mt-3 w-full rounded-lg border border-leadrs-stroke bg-white px-5 py-3 font-geist text-sm font-medium text-leadrs-text transition-colors hover:border-leadrs-brand"
                >
                    Annuler
                </button>
            </div>
        </div>
    );
};

export default WalletTopUp;
